import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ConversationService from "../services/ConversationService";
import MyAccountService from "../services/MyAccountService";

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  role: "staff",
  status: "active",
};

export default function TeamMembersView() {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [createPassword, setCreatePassword] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentAccountId, setCurrentAccountId] = useState(null);
  const [currentAccountType, setCurrentAccountType] = useState(null);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const rows = await ConversationService.getRoutingStaff();
      const normalized = Array.isArray(rows)
        ? rows
        : Array.isArray(rows?.data)
          ? rows.data
          : [];
      setStaff(normalized);
    } catch (_e) {
      setStaff([]);
      setSnackbar({
        open: true,
        message: "Failed to load team members.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const account = await MyAccountService.fetch();
        setCurrentAccountId(account?.getId?.() || null);
        setCurrentAccountType(account?.getType?.() || null);
      } catch (_e) {
        setCurrentAccountId(null);
        setCurrentAccountType(null);
      }
      await loadStaff();
    })();
  }, []);

  const notify = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const currentUserStaffRow = (staff || []).find((s) => s.id === currentAccountId);
  const isAdminType = currentAccountType === "admin" || currentAccountType === "super_admin";
  const isOrgStaffAdmin = currentUserStaffRow?.role === "admin";
  const isLikelyOwner = Boolean(currentAccountId) && !currentUserStaffRow;
  const canManageStaffCredentials = isAdminType || isOrgStaffAdmin || isLikelyOwner;

  const validateBaseForm = () => {
    if (!form.first_name?.trim() || !form.email?.trim()) {
      notify("First name and email are required.", "warning");
      return false;
    }
    return true;
  };

  const handleOpenCreate = () => {
    if (!canManageStaffCredentials) {
      notify("Only account owner/admin can create team members.", "warning");
      return;
    }
    setForm(EMPTY_FORM);
    setCreatePassword("");
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!canManageStaffCredentials) {
      notify("Only account owner/admin can create team members.", "warning");
      return;
    }
    if (!validateBaseForm()) return;
    if (!createPassword || createPassword.length < 8) {
      notify("Password must be at least 8 characters.", "warning");
      return;
    }
    setBusy(true);
    try {
      await ConversationService.createRoutingStaff({
        first_name: form.first_name.trim(),
        last_name: form.last_name?.trim() || null,
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile?.trim() || null,
        role: form.role || "staff",
        password: createPassword,
      });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setCreatePassword("");
      await loadStaff();
      notify("Team member created.");
    } catch (e) {
      const msg = e?.response?.data?.error?.message || "Failed to create team member.";
      notify(msg, "error");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenEdit = (row) => {
    if (!canManageStaffCredentials) {
      notify("Only account owner/admin can edit team members.", "warning");
      return;
    }
    setSelected(row);
    setForm({
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      email: row.email || "",
      mobile: row.mobile || "",
      role: row.role || "staff",
      status: row.status || "active",
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!selected?.id) return;
    if (!validateBaseForm()) return;
    setBusy(true);
    try {
      await ConversationService.updateRoutingStaff(selected.id, {
        first_name: form.first_name.trim(),
        last_name: form.last_name?.trim() || null,
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile?.trim() || null,
        role: form.role || "staff",
        status: form.status || "active",
      });
      setEditOpen(false);
      setSelected(null);
      await loadStaff();
      notify("Team member updated.");
    } catch (e) {
      const msg = e?.response?.data?.error?.message || "Failed to update team member.";
      notify(msg, "error");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenResetPassword = (row) => {
    if (!canManageStaffCredentials) {
      notify("Only account owner/admin can reset staff password.", "warning");
      return;
    }
    setSelected(row);
    setResetPassword("");
    setResetOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selected?.id) return;
    if (!resetPassword || resetPassword.length < 8) {
      notify("Password must be at least 8 characters.", "warning");
      return;
    }
    setBusy(true);
    try {
      await ConversationService.resetRoutingStaffPassword(selected.id, resetPassword);
      setResetOpen(false);
      setSelected(null);
      setResetPassword("");
      notify("Password reset successfully.");
    } catch (e) {
      const msg = e?.response?.data?.error?.message || "Failed to reset password.";
      notify(msg, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">Team Members</Typography>
        <Button variant="contained" onClick={handleOpenCreate} disabled={!canManageStaffCredentials}>
          Add Team Member
        </Button>
      </Box>

      <Paper sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading team members...</Typography>
          </Box>
        ) : staff.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">No team members found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{`${row.first_name || ""} ${row.last_name || ""}`.trim() || "-"}</TableCell>
                    <TableCell>{row.email || "-"}</TableCell>
                    <TableCell>{row.mobile || "-"}</TableCell>
                    <TableCell>{row.role || "staff"}</TableCell>
                    <TableCell>{row.status || "active"}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                        disabled={!canManageStaffCredentials}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => handleOpenResetPassword(row)}
                        disabled={!canManageStaffCredentials}
                      >
                        Reset Password
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={createOpen} onClose={() => !busy && setCreateOpen(false)} fullWidth maxWidth="sm">
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Add Team Member</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            <TextField label="First name" size="small" value={form.first_name} onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} />
            <TextField label="Last name" size="small" value={form.last_name} onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} />
            <TextField label="Email" size="small" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            <TextField label="Mobile (optional)" size="small" value={form.mobile} onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))} />
            <TextField label="Password" size="small" type="password" value={createPassword} onChange={(e) => setCreatePassword(e.target.value)} />
            <FormControl size="small">
              <InputLabel id="team-create-role-label">Role</InputLabel>
              <Select labelId="team-create-role-label" label="Role" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={busy}>Create</Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !busy && setEditOpen(false)} fullWidth maxWidth="sm">
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Edit Team Member</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            <TextField label="First name" size="small" value={form.first_name} onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} />
            <TextField label="Last name" size="small" value={form.last_name} onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} />
            <TextField label="Email" size="small" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            <TextField label="Mobile (optional)" size="small" value={form.mobile} onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))} />
            <FormControl size="small">
              <InputLabel id="team-edit-role-label">Role</InputLabel>
              <Select labelId="team-edit-role-label" label="Role" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel id="team-edit-status-label">Status</InputLabel>
              <Select labelId="team-edit-status-label" label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setEditOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdate} disabled={busy}>Save</Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => !busy && setResetOpen(false)} fullWidth maxWidth="xs">
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Reset Password</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {selected?.email || "Team member"}
          </Typography>
          <TextField
            label="New password"
            size="small"
            type="password"
            fullWidth
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setResetOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="contained" color="warning" onClick={handleResetPassword} disabled={busy}>Reset</Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
